// The arithmetic behind the clock, with no React in it.
//
// The legacy views (DateRangeView, DateRangeOnlyView, StartDateOnlyView,
// TimeOnlyView) each carried their own copy of these rules inside event
// handlers, next to the markup. Here they are once, on plain Dates, so that
// they can be tested without a browser and so that the four views differ only
// in what they draw.

/**
 * A Vienna wall-clock timestamp, "YYYY-MM-DDTHH:mm", as the form holds it.
 *
 * Every user of this system is in Vienna, and the core reads a timestamp
 * without a zone as Vienna time. The form therefore holds Vienna's wall clock
 * whatever zone the phone is set to: a phone abroad that held its own would
 * move the announcement by the difference on every save.
 *
 * The Date objects the clock computes with carry that wall clock in their
 * local fields (getHours and so on); their instant means nothing on its own.
 * Only parse, format, viennaLocal and viennaNow cross between the two.
 */
export type Local = string;

/** The zone every wall-clock time in the portal is in. */
export const ZONE = "Europe/Vienna";

const pad = (n: number) => String(n).padStart(2, "0");

/** format writes a Date the way the form and the core want it. */
export function format(d: Date): Local {
  return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}T${pad(d.getHours())}:${pad(d.getMinutes())}`;
}

const naive = /^(\d{4})-(\d{2})-(\d{2})(?:T(\d{2}):(\d{2})(?::(\d{2})(?:\.\d+)?)?)?$/;
const zoned = /(?:Z|[+-]\d{2}:?\d{2})$/i;

/**
 * parse reads one back; anything unreadable is null.
 *
 * A timestamp with a zone — what the core sends, RFC 3339 — is an instant and
 * becomes Vienna's wall clock at that instant, so the clock shows what the
 * core means and not what the phone's zone makes of it.
 */
export function parse(s: Local | null | undefined): Date | null {
  if (!s) return null;
  const m = naive.exec(s);
  if (m) {
    const d = new Date(+m[1]!, +m[2]! - 1, +m[3]!, +(m[4] ?? 0), +(m[5] ?? 0), +(m[6] ?? 0));
    return Number.isNaN(d.getTime()) ? null : d;
  }
  if (!zoned.test(s)) return null;
  const instant = new Date(s);
  if (Number.isNaN(instant.getTime())) return null;
  return parse(viennaLocal(instant));
}

const viennaFormat = new Intl.DateTimeFormat("en-GB", {
  timeZone: ZONE,
  year: "numeric",
  month: "2-digit",
  day: "2-digit",
  hour: "2-digit",
  minute: "2-digit",
  hourCycle: "h23",
});

/** viennaLocal is the wall clock in Vienna at an instant. */
export function viennaLocal(instant: Date): Local {
  const p: Record<string, string> = {};
  for (const part of viennaFormat.formatToParts(instant)) p[part.type] = part.value;
  // Some engines still write midnight as 24 despite h23.
  const hour = p.hour === "24" ? "00" : p.hour;
  return `${p.year}-${p.month}-${p.day}T${hour}:${p.minute}`;
}

/** viennaNow is now, as the clock computes with it: Vienna's wall clock. */
export function viennaNow(): Date {
  return parse(viennaLocal(new Date()))!;
}

export const QUARTER = 15;

/**
 * floorQuarter is the legacy default start: the quarter hour that is running
 * now. The old code computed the next quarter and subtracted fifteen minutes,
 * "to have time be within the 15 minute window".
 */
export function floorQuarter(d: Date): Date {
  const out = new Date(d);
  out.setSeconds(0, 0);
  out.setMinutes(Math.floor(out.getMinutes() / QUARTER) * QUARTER);
  return out;
}

export function addMinutes(d: Date, n: number): Date {
  return new Date(d.getTime() + n * 60_000);
}

/** addDays keeps the wall-clock time across a daylight-saving change. */
export function addDays(d: Date, n: number): Date {
  const out = new Date(d);
  out.setDate(out.getDate() + n);
  return out;
}

export function startOfDay(d: Date): Date {
  const out = new Date(d);
  out.setHours(0, 0, 0, 0);
  return out;
}

export function sameDay(a: Date, b: Date): boolean {
  return a.getFullYear() === b.getFullYear() && a.getMonth() === b.getMonth() && a.getDate() === b.getDate();
}

/** withTime is `day` at the hour and minute of `time`. */
export function withTime(day: Date, time: Date): Date {
  const out = new Date(day);
  out.setHours(time.getHours(), time.getMinutes(), 0, 0);
  return out;
}

/** withDay is `time` on the calendar day of `day`. */
export function withDay(time: Date, day: Date): Date {
  return withTime(day, time);
}

/** at sets the hour and minute of a Date, leaving the day alone. */
export function at(d: Date, hours: number, minutes: number): Date {
  const out = new Date(d);
  out.setHours(hours, minutes, 0, 0);
  return out;
}

export function minutesBetween(a: Date, b: Date): number {
  return Math.round((b.getTime() - a.getTime()) / 60_000);
}

/** calendarDaysBetween counts midnights, which is what "three days" means. */
export function calendarDaysBetween(a: Date, b: Date): number {
  return Math.round((startOfDay(b).getTime() - startOfDay(a).getTime()) / 86_400_000);
}

export function fmtTime(d: Date): string {
  return `${pad(d.getHours())}:${pad(d.getMinutes())}`;
}

/** fmtDate is DD.MM.YYYY, the format every legacy picker was given. */
export function fmtDate(d: Date): string {
  return `${pad(d.getDate())}.${pad(d.getMonth() + 1)}.${d.getFullYear()}`;
}

// --- the coupling rules --------------------------------------------------------
//
// A window is a start and, unless it is open, an end. The rules below are the
// legacy setStartTime / setEndTime / onAccept / onChange handlers: moving one
// side drags the other along just enough to keep the window valid, never
// more, so that what the person typed survives.

export type Window = { start: Date; end: Date | null };

/**
 * changeStartTime is the legacy setStartTime: the start takes the new time on
 * its own day. Should that put it after the end, the end moves by the same
 * amount, so the length is kept.
 */
export function changeStartTime(w: Window, time: Date): Window {
  const start = withTime(w.start, time);
  if (!w.end) return { start, end: null };
  const end = start > w.end ? addMinutes(w.end, minutesBetween(w.start, start)) : w.end;
  return { start, end };
}

/**
 * changeStartTimeKeepingLength is the legacy TimeOnlyView's setStartTime: the
 * start takes the new time and the end follows at the same distance, so a
 * meeting moved from ten to eleven is still an hour long.
 */
export function changeStartTimeKeepingLength(w: Window, time: Date): Window {
  const start = withTime(w.start, time);
  const end = w.end ? addMinutes(start, minutesBetween(w.start, w.end)) : null;
  return { start, end };
}

/**
 * changeEndTime is the legacy setEndTime: the end takes the new time on its
 * own day. Should that put it before the start, the start moves to a quarter
 * hour before it.
 *
 * The legacy code left the start *on* the end when the end was midnight,
 * which made a window of no length; here the start goes back a quarter hour
 * regardless, into the previous day if need be.
 */
export function changeEndTime(w: Window, time: Date): Window {
  const end = withTime(w.end ?? w.start, time);
  const start = end <= w.start ? addMinutes(end, -QUARTER) : w.start;
  return { start, end };
}

/**
 * changeRange is the legacy range picker's onAccept: the chosen days take the
 * times the window already had. A range with only a first day means "one
 * day": the end is the day after, at the start's time.
 */
export function changeRange(w: Window, days: [Date, Date | null]): Window {
  const start = withTime(days[0], w.start);
  if (days[1]) return { start, end: withTime(days[1], w.end ?? w.start) };
  return { start, end: addDays(start, 1) };
}

/**
 * changeDay is the legacy TimeOnlyView's date change: the whole window moves
 * to the new day, keeping both times and so its length.
 */
export function changeDay(w: Window, day: Date): Window {
  const start = withDay(w.start, day);
  const end = w.end ? addMinutes(start, minutesBetween(w.start, w.end)) : null;
  return { start, end };
}

/** changeStartDay moves only the start to a new day, keeping its time. */
export function changeStartDay(w: Window, day: Date): Window {
  const start = withDay(w.start, day);
  if (!w.end) return { start, end: null };
  return { start, end: start >= w.end ? addDays(start, 1) : w.end };
}

/** changeEndDay moves only the end to a new day, keeping its time. */
export function changeEndDay(w: Window, day: Date): Window {
  const end = withDay(w.end ?? w.start, day);
  return { start: end <= w.start ? addMinutes(end, -QUARTER) : w.start, end };
}

// --- the duration line ---------------------------------------------------------

export type Mode = "date" | "datetime" | "open" | "time";

/** Translate is what the views pass in; the keys are the legacy ones. */
export type Translate = (key: string) => string;

/**
 * durationText is the legacy getDuration(), the line under the pickers.
 *
 * A date announcement counts calendar days. Anything else counts the real
 * length: minutes under an hour, hours "und" minutes under a day, and days,
 * hours and minutes joined with "&" from a day upwards — those are the legacy
 * spellings, kept.
 */
export function durationText(w: Window, mode: Mode, t: Translate): string {
  if (!w.end) return t("indefinite");

  const one = (n: number, singular: string, plural: string) =>
    `${n} ${n === 1 ? t(singular) : t(plural)}`;

  if (mode === "date") {
    return one(Math.max(calendarDaysBetween(w.start, w.end), 0), "day", "days");
  }

  const total = Math.max(minutesBetween(w.start, w.end), 0);
  if (total <= 60) return one(total, "minute", "minutes");

  const days = Math.floor(total / 1440);
  const hours = Math.floor((total % 1440) / 60);
  const minutes = total % 60;

  if (days === 0) {
    if (minutes === 0) return one(hours, "hour", "hours");
    return `${one(hours, "hour", "hours")} ${t("and")} ${one(minutes, "minute", "minutes")}`;
  }
  const parts = [one(days, "day", "days")];
  if (hours !== 0) parts.push(one(hours, "hour", "hours"));
  if (minutes !== 0) parts.push(one(minutes, "minute", "minutes"));
  return parts.join(" & ");
}
